import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Download, Trash2, FileText, ShieldCheck, FilePlus, Search,
  Folder, FileSignature, Building, ScrollText
} from "lucide-react";

const CATEGORY_ICONS = {
  "Offer Letter": FileSignature,
  "Appointment Letter": FileSignature,
  "NDA": ShieldCheck,
  "Experience Letter": ScrollText,
  "Relieving Letter": ScrollText,
  "Company Policy": Building,
  "ID Proof": FileText,
  "Salary Certificate": FileText,
  "Other": FileText,
};

function fileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}
function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } }

function downloadDoc(id, filename) {
  const token = localStorage.getItem("access_token");
  fetch(`${API}/documents/${id}/download`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
    .then(blob => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click(); link.remove();
    })
    .catch(e => toast.error(e.message));
}

export default function Documents() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [tab, setTab] = useState(isReviewer ? "all" : "mine");
  const [meta, setMeta] = useState(null); // { categories, company_wide, max_bytes }
  const [all, setAll] = useState([]);
  const [mine, setMine] = useState({ personal: [], company: [] });
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ category: "Offer Letter", employee_id: "", description: "", name: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [mt] = await Promise.all([api.get("/documents/categories")]);
    setMeta(mt.data);
    if (isReviewer) {
      const [a, e] = await Promise.all([api.get("/documents"), api.get("/employees")]);
      setAll(a.data); setEmployees(e.data);
    }
    if (user?.employee_id || isReviewer) {
      const m = await api.get("/documents/mine");
      setMine(m.data);
    }
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const isCompanyCategory = (cat) => (meta?.company_wide || []).includes(cat);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file"); return; }
    if (meta && file.size > meta.max_bytes) { toast.error(`File too large. Max ${(meta.max_bytes/(1024*1024))|0} MB`); return; }
    if (!isCompanyCategory(form.category) && !form.employee_id) { toast.error("Pick an employee for this category"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", form.category);
      if (!isCompanyCategory(form.category)) fd.append("employee_id", form.employee_id);
      if (form.description) fd.append("description", form.description);
      if (form.name) fd.append("name", form.name);
      await api.post("/documents/upload", fd);
      toast.success("Document uploaded");
      setUploadOpen(false);
      setForm({ category: "Offer Letter", employee_id: "", description: "", name: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const removeDoc = async (id) => {
    if (!confirm("Delete this document permanently?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const list = tab === "all" ? all : (tab === "personal" ? mine.personal : mine.company);
  const filtered = list.filter(d =>
    (catFilter === "all" || d.category === catFilter) &&
    (!q || d.name.toLowerCase().includes(q.toLowerCase())
        || (d.employee_name || "").toLowerCase().includes(q.toLowerCase()))
  );

  const stats = useMemo(() => {
    const src = isReviewer ? all : [...mine.personal, ...mine.company];
    const c = {};
    src.forEach(d => c[d.category] = (c[d.category] || 0) + 1);
    return { total: src.length, categories: c };
  }, [isReviewer, all, mine]);

  return (
    <div className="space-y-6" data-testid="documents-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Document Vault</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Every paper, safely filed.</h1>
          <p className="mt-1 text-sm text-slate-500">Offer letters, NDAs, policies — searchable, downloadable, private.</p>
        </div>
        {isReviewer && (
          <Button onClick={() => setUploadOpen(true)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="upload-doc-btn">
            <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Upload document
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Total documents" value={stats.total} icon={Folder} />
        <StatBox label="Company policies" value={stats.categories["Company Policy"] || 0} icon={Building} />
        <StatBox label="Offer letters" value={stats.categories["Offer Letter"] || 0} icon={FileSignature} />
        <StatBox label="NDAs" value={stats.categories["NDA"] || 0} icon={ShieldCheck} />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or employee" className="pl-9 h-10" data-testid="doc-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-56 h-10" data-testid="doc-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(meta?.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {isReviewer && <TabsTrigger value="all" data-testid="tab-all-docs">All documents</TabsTrigger>}
          {user?.employee_id && <TabsTrigger value="personal" data-testid="tab-my-docs">My documents</TabsTrigger>}
          <TabsTrigger value="company" data-testid="tab-company-docs">Company policies</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map(d => {
              const Icon = CATEGORY_ICONS[d.category] || FileText;
              return (
                <Card key={d.id} className="p-4 rounded-xl border-slate-200/60 flex items-start gap-3" data-testid={`doc-${d.id}`}>
                  <div className="h-11 w-11 rounded-lg bg-red-50 text-[#DC2626] grid place-items-center shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
                      <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{d.category}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.employee_name ? `For ${d.employee_name} · ` : "Company-wide · "}
                      {fileSize(d.size_bytes)} · Uploaded by {d.uploaded_by} on {fmt(d.uploaded_at)}
                    </div>
                    {d.description && <div className="text-xs text-slate-600 mt-1">{d.description}</div>}
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadDoc(d.id, d.name)} data-testid={`download-doc-${d.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Download
                      </Button>
                      {isReviewer && (
                        <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => removeDoc(d.id)} data-testid={`delete-doc-${d.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
            {filtered.length === 0 && <div className="md:col-span-2 text-sm text-slate-500 py-12 text-center border border-dashed rounded-xl">No documents in this view.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
          <form onSubmit={upload} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v, employee_id: isCompanyCategory(v) ? "" : form.employee_id})}>
                <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500">{isCompanyCategory(form.category) ? "Visible to every employee." : "Visible only to this employee and HR."}</div>
            </div>
            {!isCompanyCategory(form.category) && (
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <Select value={form.employee_id} onValueChange={v => setForm({...form, employee_id: v})}>
                  <SelectTrigger data-testid="upload-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>File (PDF, DOCX, image · max 10MB)</Label>
              <Input ref={fileRef} type="file" onChange={e => setFile(e.target.files?.[0])} accept="application/pdf,image/*,.doc,.docx,.txt" data-testid="upload-file" required />
              {file && <div className="text-xs text-slate-500">{file.name} · {fileSize(file.size)}</div>}
            </div>
            <div className="space-y-1.5">
              <Label>Display name (optional)</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Defaults to file name" />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="flex justify-end"><Button disabled={busy} type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="upload-submit"><Upload className="h-4 w-4 mr-1.5" strokeWidth={1.8} />Upload</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, icon: Icon }) {
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-lg grid place-items-center text-[#DC2626] bg-red-50"><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}
